import { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { ListOrdered, Download, Loader2, Search } from "lucide-react";
import { API, useAuth } from "../App";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Badge } from "../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { readWorkspace } from "../utils/workspace";
import { DocketTable, downloadDocketCsv } from "../components/DocketTable";
import { DateInput } from "../components/ui/date-input";

const fmt = (d) => { if (!d) return "—"; const p = d.split("-"); return p.length === 3 ? `${p[2]}/${p[1]}/${p[0]}` : d; };

/** «Εκθέματα SOLON»: τα εκθέματα δικασίμων που αντλήθηκαν αυτόματα (TrackDocket) — λίστα ανά δικάσιμο/πινάκιο, προβολή, CSV, εκτύπωση. */
export default function Dockets() {
  const { user } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [courtType, setCourtType] = useState(() => readWorkspace(user)?.court_type || "");
  const [q, setQ] = useState("");
  const [dateFrom, setDateFrom] = useState(() => new Date().toISOString().split("T")[0]);
  const [open, setOpen] = useState(null);
  const [detail, setDetail] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom || undefined, court_type: courtType || undefined, q: q || undefined };
      setData((await axios.get(`${API}/solon/dockets`, { params })).data);
    } catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα φόρτωσης εκθεμάτων"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, [courtType, dateFrom]); // eslint-disable-line react-hooks/exhaustive-deps

  const show = async (id) => {
    if (open === id) { setOpen(null); setDetail(null); return; }
    setOpen(id); setDetail(null);
    try { setDetail((await axios.get(`${API}/solon/dockets/${id}`)).data); }
    catch (e) { toast.error(e.response?.data?.detail || "Σφάλμα"); }
  };
  const download = downloadDocketCsv;

  const items = data?.items || [];
  const byDate = items.reduce((acc, it) => { (acc[it.hearing_date] = acc[it.hearing_date] || []).push(it); return acc; }, {});
  return (
    <div className="space-y-6" data-testid="dockets-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 flex items-center gap-2"><ListOrdered className="w-6 h-6 text-sky-700" />Εκθέματα SOLON</h1>
          <p className="text-slate-500 text-sm">Εκθέματα Ανάρτησης δικασίμων από το extapps.solon.gov.gr — αντλούνται αυτόματα {data?.settings?.days_before ?? 10} ημέρες πριν από κάθε δικάσιμο ({data?.settings?.court_office_label || "…"}).{data?.settings?.last_run_at ? ` Τελευταία άντληση: ${new Date(data.settings.last_run_at).toLocaleString("el-GR")}.` : ""}</p>
        </div>
        {!data?.settings?.enabled && data && <Badge variant="secondary" data-testid="dockets-disabled-badge">Αυτόματη άντληση ανενεργή (Διαχείριση → Εργαλεία)</Badge>}
      </div>
      <Card>
        <CardContent className="pt-4 flex flex-wrap items-end gap-3">
          <div className="space-y-1"><label className="text-xs text-slate-500">Δικάσιμοι από</label><DateInput value={dateFrom} onChange={(e) => setDateFrom(e.target.value)} className="w-40" data-testid="dockets-date-from" /></div>
          <div className="space-y-1"><label className="text-xs text-slate-500">Τύπος</label>
            <Select value={courtType || "all"} onValueChange={(v) => setCourtType(v === "all" ? "" : v)}>
              <SelectTrigger className="w-40" data-testid="dockets-court-type"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="all">Όλα</SelectItem><SelectItem value="Μονομελές">Μονομελές</SelectItem><SelectItem value="Πολυμελές">Πολυμελές</SelectItem></SelectContent>
            </Select></div>
          <div className="space-y-1 flex-1 min-w-[200px]"><label className="text-xs text-slate-500">Πινάκιο / αίθουσα / διαδικασία</label>
            <div className="flex gap-2"><Input value={q} onChange={(e) => setQ(e.target.value)} onKeyDown={(e) => e.key === "Enter" && load()} placeholder="π.χ. ΕΝΕΜ3, ΕΛΕΥΣΙΝΑΣ" data-testid="dockets-search" />
              <Button variant="outline" onClick={load} data-testid="dockets-search-btn"><Search className="w-4 h-4" /></Button></div></div>
          <span className="text-xs text-slate-500" data-testid="dockets-count">{items.length} εκθέματα</span>
        </CardContent>
      </Card>
      {loading ? <div className="py-10 text-center text-slate-500"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Φόρτωση…</div>
        : items.length === 0 ? <Card><CardContent className="py-10 text-center text-slate-500" data-testid="dockets-empty">Δεν υπάρχουν αποθηκευμένα εκθέματα για το διάστημα. Η άντληση γίνεται αυτόματα {data?.settings?.days_before ?? 10} ημέρες πριν από κάθε δικάσιμο· ο διαχειριστής μπορεί να την εκτελέσει άμεσα από Διαχείριση → Εργαλεία.</CardContent></Card>
        : Object.keys(byDate).sort().map((date) => (
          <Card key={date} data-testid={`dockets-date-${date}`}>
            <CardHeader className="py-3"><CardTitle className="text-base">Δικάσιμος {fmt(date)}</CardTitle><CardDescription>{byDate[date].length} πινάκια · {byDate[date].reduce((s, x) => s + (x.line_count || 0), 0)} υποθέσεις στο έκθεμα</CardDescription></CardHeader>
            <CardContent className="space-y-2">
              {byDate[date].map((d) => (
                <div key={d.id} className="rounded-lg border" data-testid={`docket-${d.id}`}>
                  <div className="flex flex-wrap items-center gap-2 p-3 cursor-pointer hover:bg-slate-50" onClick={() => show(d.id)} data-testid={`docket-toggle-${d.id}`}>
                    <Badge variant="outline">{d.court_type || "—"}</Badge>
                    <span className="font-medium">{d.docket_label}</span>
                    <span className="text-xs text-slate-500">{d.proc_label}</span>
                    <span className="text-xs text-slate-600 ml-auto">{d.time} · {d.room}</span>
                    <Badge className="bg-sky-700">{d.line_count} υποθ.</Badge>
                    {d.matched_case_ids?.length > 0 && <Badge className="bg-emerald-600" data-testid={`docket-matched-${d.id}`}>{d.matched_case_ids.length} στο σύστημα</Badge>}
                    <Button size="sm" variant="ghost" onClick={(e) => { e.stopPropagation(); download(d); }} data-testid={`docket-csv-${d.id}`}><Download className="w-4 h-4" /></Button>
                  </div>
                  {open === d.id && (
                    <div className="border-t p-3" data-testid={`docket-detail-${d.id}`}>
                      <DocketTable detail={detail} testId={`docket-${d.id}`} />
                    </div>
                  )}
                </div>
              ))}
            </CardContent>
          </Card>
        ))}
    </div>
  );
}
